import type { ComponentType } from 'react';

/**
 * TCR-1189 — tách khỏi `App.tsx` để `RouteErrorBoundary` dùng được
 * `isLazyChunkError` mà không tạo circular import (App.tsx đang import
 * `RouteErrorBoundary`).
 *
 * Mirrors React.lazy's own constraint: ComponentType<any> needed for contravariance with concrete prop types
 */
const LAZY_CHUNK_RETRY_DELAYS_MS = [250, 1_000, 2_000] as const;

export function isLazyChunkError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    const message = error.message.toLowerCase();
    return error.name === 'ChunkLoadError' || (
        error instanceof TypeError && (
            message.includes('failed to fetch dynamically imported module') ||
            message.includes('importing a module script failed') ||
            message.includes('dynamically imported module') ||
            // Safari reports a failed dynamic import as simply "Load failed".
            message.includes('load failed')
        )
    );
}

export async function loadLazyModule<T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>,
): Promise<{ default: T }> {
    let lastError: unknown;
    for (let attempt = 0; attempt <= LAZY_CHUNK_RETRY_DELAYS_MS.length; attempt += 1) {
        try {
            const loaded = await factory();
            return loaded;
        } catch (error) {
            lastError = error;
            if (!isLazyChunkError(error) || attempt === LAZY_CHUNK_RETRY_DELAYS_MS.length) {
                throw error;
            }
            await new Promise<void>((resolve) => {
                setTimeout(resolve, LAZY_CHUNK_RETRY_DELAYS_MS[attempt]);
            });
        }
    }
    throw lastError;
}
